import type {
  Bonus,
  DeliveryItem,
  FaqItem,
  OfferRow,
  PainPoint,
  Testimonial,
  Volume,
} from '../types/content'

export const CHECKOUT_URL = 'https://renal-it.impultienda.ar/checkout/'

export const PRICE = '€19'
export const TOTAL_VALUE = '€292'

export const MOCKUP_IMAGE =
  'https://cdn.magicpatterns.com/uploads/sKK2YFyjJiXmLJjy73FDaa/SOLDADURA-IT-2.png'

export const KARITO_PHOTO =
  'https://cdn.magicpatterns.com/patterns/generated-images/c6d48fd3-2f71-4334-acd9-b8c9b3081a68.jpg'

export const heroStats = [
  { value: '+300', label: 'Pagine cliniche' },
  { value: '6', label: 'Volumi specializzati' },
  { value: '5', label: 'Bonus premium' },
  { value: '3 min', label: 'Per algoritmo' },
  { value: '+1.000', label: 'Medici' },
]

export const beforeItems = [
  'Passi più tempo a cercare risposte che a visitare il paziente.',
  'Apri KDIGO, UpToDate e diverse linee guida… e alla fine hai ancora dei dubbi.',
  'Non sai se quella riduzione della TFG sia realmente preoccupante o se possa essere monitorata.',
  'Sospendi farmaci per paura, anche quando potrebbero proteggere la funzione renale.',
  'Invii pazienti allo specialista “per sicurezza”, senza sapere con certezza se sia davvero necessario.',
]

export const afterItems = [
  'Apri l’algoritmo corretto e segui un unico percorso clinico.',
  'Identifichi lo stadio della malattia renale in pochi minuti.',
  'Sai esattamente quali esami richiedere e come interpretarli.',
  'Sai cosa iniziare, cosa mantenere e cosa sospendere.',
  'Sai cosa monitorare e quando richiamare il paziente.',
  'Sai quando è necessario inviare il paziente al nefrologo… e quando non è necessario.',
]

export const painPoints: PainPoint[] = [
  {
    id: 'creatinina',
    title: 'Vedi una creatinina elevata',
    lines: [
      'E non sai se ti trovi davanti a un’urgenza o a un dato che può essere monitorato.',
      'Temi di sottovalutare un paziente che sta perdendo funzione renale oppure di reagire in modo eccessivo a un singolo risultato di laboratorio.',
    ],
  },
  {
    id: 'tempo',
    title: 'Quello che dovrebbe richiedere pochi minuti consuma metà della visita',
    lines: [
      'Passi da una linea guida all’altra cercando una risposta pratica mentre il paziente aspetta una decisione.',
      'Sai che l’informazione esiste. Ma non hai il tempo di cercarla.',
    ],
  },
  {
    id: 'farmaci',
    title: 'Ogni farmaco crea un nuovo dubbio',
    lines: [
      'Hai paura di sospendere un farmaco che protegge il rene… oppure di mantenerne uno che potrebbe creare problemi.',
      'ACE-inibitori. Sartani. Metformina. Inibitori SGLT2.',
      'Cosa dovresti fare quando la funzione renale cambia?',
    ],
  },
  {
    id: 'potassio',
    title: 'Un potassio elevato cambia l’intera visita',
    lines: [
      'Vedi il risultato. Ora devi decidere: puoi gestirlo ambulatorialmente? Devi ripetere l’esame? Devi modificare la terapia? Il paziente deve essere valutato urgentemente?',
      'E il paziente è davanti a te, aspettando una risposta.',
    ],
  },
  {
    id: 'progressione',
    title: 'Non hai chiaro quando ci sia una vera progressione renale',
    lines: [
      'Arriva un nuovo esame di laboratorio. I valori sono cambiati.',
      'Ma si tratta di una vera progressione? Di una variazione temporanea? O di un risultato che richiede un approccio diverso?',
      'Ogni nuovo esame sembra creare più domande che risposte.',
    ],
  },
  {
    id: 'dubbio',
    title: 'Il paziente se ne va. Il dubbio resta.',
    lines: [
      'La visita è terminata. Ma ore dopo continui a chiederti: “Ho davvero preso la decisione migliore per questo paziente?”',
      'Non dovresti portarti dietro questa incertezza dopo ogni visita nefrologica.',
    ],
  },
]

export const pathSteps = [
  'Vedi il dato.',
  'Identifica il percorso clinico.',
  'Prendi la decisione.',
  'Tratta.',
  'Monitora.',
  'Sai quando inviare allo specialista.',
]

export const volumes: Volume[] = [
  {
    id: 'vol-1',
    label: 'Volume I',
    title: 'Il Radar Renale™',
    promise:
      'Individua la malattia renale prima che importanti segnali d’allarme vengano trascurati.',
    intro:
      'Trasforma un’alterazione della creatinina o della TFG in un percorso diagnostico e una stadiazione strutturata.',
    listTitle: 'Imparerai a:',
    items: [
      'Interpretare creatinina e TFG nel contesto clinico.',
      'Identificare e classificare la malattia renale cronica.',
      'Comprendere le categorie G e A.',
      'Riconoscere i risultati che richiedono ulteriori valutazioni.',
      'Identificare i pazienti a maggiore rischio di progressione.',
      'Utilizzare algoritmi diagnostici visivi.',
      'Passare da dato di laboratorio → interpretazione → azione successiva.',
    ],
    goal: 'Capire cosa ti stanno dicendo i valori renali — e sapere cosa fare dopo.',
    accent: '#1B7A3D',
  },
  {
    id: 'vol-2',
    label: 'Volume II',
    title: 'Lo Scudo Nefroprotettivo™',
    promise:
      'Proteggi la funzione renale senza sospendere inutilmente i trattamenti che possono aiutare il tuo paziente.',
    intro:
      'Un sistema pratico per orientarti nelle principali terapie e strategie utilizzate per rallentare la progressione della malattia renale.',
    listTitle: 'Imparerai come gestire:',
    items: [
      'ACE-inibitori',
      'Sartani',
      'Inibitori SGLT2',
      'Albuminuria',
      'Variazioni della creatinina dopo l’inizio della terapia',
      'Quando continuare',
      'Quando rivalutare',
      'Quando considerare una modifica terapeutica',
      'Come organizzare il monitoraggio',
    ],
    goal:
      'Smettere di reagire automaticamente a ogni variazione di laboratorio e iniziare a gestire la nefroprotezione con un percorso strutturato.',
    accent: '#0F5C33',
  },
  {
    id: 'vol-3',
    label: 'Volume III',
    title: 'Il Controllore dell’Equilibrio Interno™',
    promise:
      'Gestisci potassio, diuretici ed equilibrio acido-base senza bloccarti davanti a un laboratorio alterato.',
    intro:
      'Un sistema pratico per affrontare le più comuni alterazioni elettrolitiche e metaboliche nel paziente con malattia renale.',
    listTitle: 'Imparerai a gestire:',
    items: [
      'Iperkaliemia',
      'Alterazioni dell’equilibrio acido-base',
      'Uso dei diuretici',
      'Gestione dei liquidi',
      'Alterazioni elettrolitiche comuni',
      'Segnali d’allarme',
      'Quando la gestione ambulatoriale può essere appropriata',
      'Quando considerare un’escalation o una valutazione specialistica',
    ],
    goal:
      'Trasformare un valore renale alterato in un’azione strutturata invece che in una nuova fonte di incertezza.',
    accent: '#1F6F4A',
  },
  {
    id: 'vol-4',
    label: 'Volume IV',
    title: 'Il Triangolo Cardio-Reno-Metabolico™',
    promise: 'Gestisci il paziente in cui la malattia renale non esiste da sola.',
    intro:
      'Diabete, ipertensione, insufficienza cardiaca, alterazioni metaboliche e malattia renale spesso convivono nello stesso paziente. Questo volume ti aiuta a organizzare queste priorità in un approccio clinico coerente.',
    listTitle: 'Imparerai come affrontare:',
    items: [
      'Malattia renale + diabete',
      'Malattia renale + ipertensione',
      'Malattia renale + insufficienza cardiaca',
      'Proteinuria e rischio cardiovascolare',
      'Comorbilità metaboliche',
      'Considerazioni farmacologiche',
      'Priorità terapeutiche',
      'Decisioni di follow-up',
    ],
    goal:
      'Smettere di trattare singoli valori di laboratorio e iniziare a gestire il paziente cardio-renale-metabolico nel suo insieme.',
    accent: '#123A5C',
  },
  {
    id: 'vol-5',
    label: 'Volume V',
    title: 'Il Filtro Maestro™',
    promise:
      'Sai cosa monitorare, quando farlo e quando è necessaria una valutazione specialistica.',
    intro:
      'Un sistema pratico per il follow-up e l’invio al nefrologo del paziente con malattia renale cronica.',
    listTitle: 'Imparerai a gestire:',
    items: [
      'Anemia',
      'Alterazioni di calcio e fosforo',
      'Vitamina D',
      'Acidosi metabolica',
      'Marcatori di progressione',
      'Frequenza del follow-up',
      'Criteri di invio al nefrologo',
      'Informazioni da includere nella richiesta di consulenza',
    ],
    goal: 'Sapere quando gestire, quando monitorare e quando inviare allo specialista.',
    accent: '#2E2A63',
  },
  {
    id: 'vol-6',
    label: 'Volume VI',
    title: 'Strumenti Clinici e Checklist™',
    promise:
      'Trasforma l’intero sistema in strumenti pratici da utilizzare durante la visita.',
    intro: 'Il tuo arsenale di consultazione rapida.',
    listTitle: 'Comprende:',
    items: [
      'Tabelle dei farmaci in base alla funzione renale',
      'Checklist decisionali',
      'Modelli di follow-up',
      'Criteri di invio allo specialista',
      'Scale e strumenti di consultazione rapida',
      'Materiale educativo per il paziente',
      'Checklist per la visita',
      'Pagine di riferimento rapido',
    ],
    goal: 'Avere lo strumento giusto davanti a te quando devi prendere una decisione.',
    accent: '#7A3A18',
  },
]

export const deliveryItems: DeliveryItem[] = [
  {
    id: 'del-1',
    title: 'I 6 Volumi Premium di Percorso Malattia Renale™',
    lines: [
      'Non sono libri da leggere dalla prima all’ultima pagina. Sono strumenti clinici progettati per aiutarti a prendere decisioni in pochi minuti.',
      'Ogni volume affronta una parte fondamentale della gestione della malattia renale, dalla valutazione iniziale fino al monitoraggio a lungo termine.',
    ],
  },
  {
    id: 'del-2',
    title: 'Algoritmi Clinici Decisionali™',
    lines: [
      'Smetti di cercare la risposta tra centinaia di pagine di linee guida.',
      'Ogni algoritmo risponde a una sola domanda pratica: “Vedo questo… cosa faccio adesso?”',
      'Passo dopo passo. In modo chiaro. Senza perdere tempo.',
    ],
  },
  {
    id: 'del-3',
    title: 'Protocolli di Nefroprotezione™',
    lines: [
      'Impara quando proteggere il rene e quando una variazione della creatinina non significa necessariamente che devi sospendere un trattamento.',
      'Percorsi strutturati per ridurre l’incertezza nelle decisioni terapeutiche.',
    ],
  },
  {
    id: 'del-4',
    title: 'Percorsi di Azione Immediata™',
    lines: [
      'Quando arriva un laboratorio alterato, non dovrai aprire tre diverse linee guida.',
      'Segui il percorso: Diagnosi → Trattamento → Monitoraggio → Invio. Nell’ordine corretto.',
    ],
  },
  {
    id: 'del-5',
    title: 'Checklist Cliniche Intelligenti™',
    lines: [
      'Ogni visita avrà un processo chiaro: cosa chiedere, cosa richiedere, cosa interpretare, cosa trattare, cosa monitorare e quando rivedere il paziente.',
      'Niente di importante viene lasciato al caso.',
    ],
  },
]

export const bonuses: Bonus[] = [
  {
    id: 'bonus-1',
    label: 'Bonus #1',
    title: 'Protocollo Iperkaliemia Zero™',
    promise: 'Non bloccarti mai più davanti a un potassio elevato.',
    listTitle: 'Saprai come orientarti tra:',
    items: [
      'Quando osservare',
      'Quando rivalutare',
      'Quando modificare i farmaci',
      'Quando intervenire',
      'Quando considerare un invio urgente',
    ],
    value: '€37',
    image:
      'https://cdn.magicpatterns.com/uploads/srBFngRMBeLnPRWnJU3YyL/BONUS_1_RENAL-IT.png',
  },
  {
    id: 'bonus-2',
    label: 'Bonus #2',
    title: 'Vademecum Renale Intelligente™',
    promise: 'Un riferimento rapido per i farmaci più utilizzati e la funzione renale.',
    intro:
      'Informazioni organizzate per aiutarti a orientarti più rapidamente nella gestione farmacologica del paziente con funzione renale ridotta.',
    listTitle: 'Include:',
    items: [
      'Antibiotici',
      'Farmaci antidiabetici',
      'Anticoagulanti',
      'Analgesici',
      'Antipertensivi',
      'Altri farmaci comunemente prescritti',
    ],
    value: '€47',
    image:
      'https://cdn.magicpatterns.com/uploads/8b4Hn2vD3ejTuJ8A5jVMy8/BONUS_2_RENAL-IT.png',
  },
  {
    id: 'bonus-3',
    label: 'Bonus #3',
    title: 'Script per la Visita Renale™',
    promise: 'Sappi cosa dire quando il paziente ti pone le domande più difficili.',
    intro: 'Cosa rispondere quando chiede:',
    quotes: [
      '“Dovrò fare la dialisi?”',
      '“I miei reni possono recuperare?”',
      '“Quanto è grave?”',
      '“Perché devo prendere questi farmaci?”',
      '“Cosa posso fare per evitare che peggiori?”',
    ],
    listTitle: '',
    items: [
      'Framework di comunicazione pratici per spiegare concetti renali complessi in modo semplice, chiaro e comprensibile.',
    ],
    value: '€27',
    image:
      'https://cdn.magicpatterns.com/uploads/sCtqrWVFDrxF5UJ3FTZ6Wc/BONUS_3_RENAL-IT.png',
  },
  {
    id: 'bonus-4',
    label: 'Bonus #4',
    title: 'I 25 Errori Renali Critici™',
    promise:
      'Riconosci gli errori clinici più comuni prima che possano compromettere la gestione del paziente.',
    intro:
      'Una guida pratica alla prevenzione degli errori nelle aree più delicate della gestione della malattia renale:',
    listTitle: '',
    items: [
      'Diagnosi e stadiazione',
      'Interpretazione di creatinina e TFG',
      'Gestione farmacologica',
      'Nefroprotezione',
      'Monitoraggio e follow-up',
      'Invio al nefrologo',
    ],
    value: '€47',
    image:
      'https://cdn.magicpatterns.com/uploads/iEywD1hPhBdniqiaMNz3Gh/BONUS_4_RENAL-IT.png',
  },
  {
    id: 'bonus-5',
    label: 'Bonus #5',
    title: 'Checklist Maestra della Visita Renale™',
    promise:
      'Non dimenticare mai più un esame, un’indicazione o un criterio importante durante la visita.',
    intro:
      'Una checklist pratica e stampabile progettata per aiutarti a strutturare la visita renale dall’inizio alla fine.',
    listTitle: 'Include:',
    items: [
      'Valutazione iniziale',
      'Controllo degli esami di laboratorio',
      'Revisione dei farmaci',
      'Decisioni terapeutiche',
      'Monitoraggio',
      'Follow-up',
      'Criteri di invio al nefrologo',
      'Red flags',
      'Documentazione clinica',
    ],
    value: '€37',
    image:
      'https://cdn.magicpatterns.com/uploads/1vxvAwh6xVZJ6pxHJ4kYGD/BONUS_5_RENAL-IT.png',
  },
]

export const offerRows: OfferRow[] = [
  { label: 'Percorso Malattia Renale™ — 6 Volumi Premium', value: '€97' },
  { label: 'Protocollo Iperkaliemia Zero™', value: '€37' },
  { label: 'Vademecum Renale Intelligente™', value: '€47' },
  { label: 'Script per la Visita Renale™', value: '€27' },
  { label: 'I 25 Errori Renali Critici™', value: '€47' },
  { label: 'Checklist Maestra della Visita Renale™', value: '€37' },
]

export const testimonials: Testimonial[] = [
  {
    id: 't-1',
    quote:
      'Prima inviavo molti pazienti dal nefrologo semplicemente perché non ero abbastanza sicuro della gestione. Ora ho un quadro molto più chiaro di quali pazienti posso gestire e di quando è realmente necessario lo specialista.',
    name: 'Dott. Ricardo M.',
    location: 'Messico',
    flag: '🇲🇽',
    photo:
      'https://cdn.magicpatterns.com/patterns/generated-images/c9ddbad3-0b1e-4d85-b355-cdd02863bcfe.jpg',
  },
  {
    id: 't-2',
    quote:
      'Lo Scudo Nefroprotettivo™ ha cambiato completamente il mio modo di affrontare le decisioni terapeutiche quando la creatinina cambia. Ho smesso di reagire automaticamente e ho iniziato a valutare il quadro clinico nel suo insieme.',
    name: 'Dott.ssa Elena L.',
    location: 'Colombia',
    flag: '🇨🇴',
    photo:
      'https://cdn.magicpatterns.com/patterns/generated-images/35977cb9-5b03-4689-88a8-e78fca6b5e25.jpg',
  },
  {
    id: 't-3',
    quote:
      'Il Vademecum Renale Intelligente™ è una delle risorse che utilizzo di più. Avere le informazioni organizzate per una consultazione rapida mi fa risparmiare moltissimo tempo.',
    name: 'Dott. Esteban P.',
    location: 'Spagna',
    flag: '🇪🇸',
    photo:
      'https://cdn.magicpatterns.com/patterns/generated-images/d28ee23a-1cd3-4802-ac1b-81fee4069d43.jpg',
  },
  {
    id: 't-4',
    quote:
      'Gli Script per la Visita Renale mi hanno aiutato a spiegare la malattia renale ai pazienti con molta più sicurezza. Le domande sulla dialisi, sulla progressione e sui farmaci sono molto più facili da affrontare.',
    name: 'Dott.ssa Mariana L.',
    location: 'Argentina',
    flag: '🇦🇷',
    photo:
      'https://cdn.magicpatterns.com/patterns/generated-images/75887a0a-dd5c-409b-bb2e-3db33696ea9d.jpg',
  },
]

export const faqItems: FaqItem[] = [
  {
    id: 'faq-1',
    question: 'Come riceverò il materiale?',
    answer: [
      'Subito dopo il pagamento riceverai l’accesso digitale per scaricare tutti i volumi e i bonus.',
      'In pochi minuti potrai iniziare a utilizzarli.',
    ],
  },
  {
    id: 'faq-2',
    question: 'È un prodotto fisico?',
    answer: [
      'No. Percorso Malattia Renale™ è 100% digitale.',
      'Puoi utilizzare i materiali da computer, tablet o smartphone e stampare le risorse che desideri avere a disposizione durante la pratica clinica.',
    ],
  },
  {
    id: 'faq-3',
    question: 'È un abbonamento?',
    answer: [
      `No. È un pagamento unico di ${PRICE}.`,
      'Non ci sono abbonamenti mensili, rinnovi automatici o costi ricorrenti. L’accesso è a vita.',
    ],
  },
  {
    id: 'faq-4',
    question: 'È basato sulle linee guida cliniche?',
    answer: [
      'Il sistema è strutturato sulla base delle principali raccomandazioni internazionali sulla malattia renale e della pratica clinica basata sulle evidenze.',
      'Poiché le raccomandazioni cliniche possono cambiare, verifica sempre le linee guida locali più aggiornate, le informazioni ufficiali sui farmaci e il tuo giudizio clinico professionale prima di prendere decisioni terapeutiche.',
    ],
  },
  {
    id: 'faq-5',
    question: 'È pensato solo per nefrologi?',
    answer: [
      'No. Percorso Malattia Renale™ è stato progettato per i medici che incontrano regolarmente pazienti con problemi renali ma che non sono necessariamente specialisti in nefrologia.',
      'È particolarmente adatto a:',
    ],
    items: [
      'Medici di medicina generale',
      'Internisti',
      'Medici di famiglia',
      'Medici di assistenza primaria',
      'Specializzandi',
      'Professionisti sanitari coinvolti nella gestione del paziente renale',
    ],
  },
  {
    id: 'faq-6',
    question: 'Devo avere una formazione avanzata in nefrologia?',
    answer: [
      'No. Il sistema è strutturato per rendere più semplici da consultare i percorsi decisionali renali complessi.',
      'Non devi memorizzare ogni singola raccomandazione. L’obiettivo è aiutarti a organizzare le informazioni in un processo clinico più chiaro.',
    ],
  },
  {
    id: 'faq-7',
    question: 'Sostituisce il giudizio clinico o la consulenza del nefrologo?',
    answer: [
      'No. Percorso Malattia Renale™ è uno strumento educativo e di supporto alle decisioni cliniche.',
      'Non sostituisce il giudizio professionale, le linee guida aggiornate, le informazioni ufficiali sui farmaci o il ricorso appropriato allo specialista.',
    ],
  },
]

export const purchaseNotifications = [
  { name: 'Dott. Andrés G.', country: 'Messico', flag: '🇲🇽' },
  { name: 'Dott.ssa Lucía F.', country: 'Spagna', flag: '🇪🇸' },
  { name: 'Dott. Javier R.', country: 'Argentina', flag: '🇦🇷' },
  { name: 'Dott.ssa Camila T.', country: 'Colombia', flag: '🇨🇴' },
  { name: 'Dott. Miguel S.', country: 'Perù', flag: '🇵🇪' },
  { name: 'Dott.ssa Valentina O.', country: 'Cile', flag: '🇨🇱' },
  { name: 'Dott. Fernando D.', country: 'Ecuador', flag: '🇪🇨' },
  { name: 'Dott.ssa Paula M.', country: 'Uruguay', flag: '🇺🇾' },
  { name: 'Dott. Rafael C.', country: 'Repubblica Dominicana', flag: '🇩🇴' },
  { name: 'Dott.ssa Sofía N.', country: 'Costa Rica', flag: '🇨🇷' },
]
